import { EFillPolicy, EOrderExpiry } from '@aqx-core/common/api/order';
import { i18n } from '@aqx-core/common/i18n';

import { EUIOrderTypeLabel, TUITradeConfirmationItemData } from './types';

export const TRADE_FORM_ORDER_TYPE_LABEL_DROPDOWN_LIST_DEFAULT = [
  EUIOrderTypeLabel.MARKET,
  EUIOrderTypeLabel.LIMIT,
  EUIOrderTypeLabel.STOP
];
export const TRADE_FORM_ORDER_TYPE_LABEL_DROPDOWN_LIST_MT5 = [
  EUIOrderTypeLabel.MARKET,
  EUIOrderTypeLabel.LIMIT,
  EUIOrderTypeLabel.STOP,
  EUIOrderTypeLabel.STOP_LIMIT
];
export const TRADE_FORM_ORDER_TYPE_LABEL_DROPDOWN_LIST_CENTROID = [
  EUIOrderTypeLabel.MARKET,
  EUIOrderTypeLabel.LIMIT,
  EUIOrderTypeLabel.STOP,
  EUIOrderTypeLabel.STOP_LIMIT
];
export const TRADE_FORM_ORDER_TYPE_LABEL_DROPDOWN_LIST_HORIZON = [
  EUIOrderTypeLabel.MARKET,
  EUIOrderTypeLabel.MARKET_TO_LIMIT,
  EUIOrderTypeLabel.LIMIT,
  EUIOrderTypeLabel.STOP,
  EUIOrderTypeLabel.STOP_LIMIT
];

export const CASH_FILL_POLICY_DROPDOWN_LIST_CENTROID = [
  EFillPolicy.GTC,
  // Temporarily remove GTD option for Centroid as it's not supported in the backend yet, will add back once it's supported
  // EFillPolicy.GTD
  EFillPolicy.FOK,
  EFillPolicy.IOC
];
export const CASH_FILL_POLICY_DROPDOWN_LIST_MARKET = [EFillPolicy.FOK, EFillPolicy.IOC];
export const CASH_FILL_POLICY_DROPDOWN_LIST_LIMIT = [EFillPolicy.RETURN];

export const CASH_FILL_POLICY_DROPDOWN_LIST_HORIZON = [
  EFillPolicy.DAY,
  EFillPolicy.FOK,
  EFillPolicy.IOC,
  EFillPolicy.GTC,
  EFillPolicy.GTD
];

export const TRADE_EXPIRY_DROPDOWN_LIST = [EOrderExpiry.GTC, EOrderExpiry.GTD];
export const TRADE_EXPIRY_DROPDOWN_LIST_MT5 = [
  EOrderExpiry.GTC,
  EOrderExpiry.GTD,
  EOrderExpiry.SPECIFIED_DAY,
  EOrderExpiry.SPECIFIED_TIMESTAMP
];

export const derivativeFillPolicyDropDownList = [EFillPolicy.IOC];

export const getTradeBasePricesDataKeyHorizon = (): TUITradeConfirmationItemData[] => [
  { key: 'lotSize', label: () => i18n.t('Quantity') }
];

export const getTradeBasePricesDataKeyMT4 = (): TUITradeConfirmationItemData[] => [
  { key: 'lotSize', label: () => i18n.t('Size') },
  { key: 'units', label: () => i18n.t('Units') }
];
export const getTradeBasePricesDataKeyMT5 = (): TUITradeConfirmationItemData[] => [
  { key: 'lotSize', label: () => i18n.t('Volume') },
  { key: 'units', label: () => i18n.t('Units') }
];

export const getTradeBasePricesDataKeyCentroid = (): TUITradeConfirmationItemData[] => [
  { key: 'lotSize', label: () => i18n.t('Volume') }
];

export const getTradeLimitPricesDataKeyHorizon = (): TUITradeConfirmationItemData[] => [
  { key: 'price', label: () => i18n.t('Limit Price') }
];
export const getTradeLimitPricesDataKeyMetatrader = (): TUITradeConfirmationItemData[] => [
  { key: 'price', label: () => i18n.t('Price') }
];

export const getTradeStopPricesDataKeyHorizon = (): TUITradeConfirmationItemData[] => [
  { key: 'price', label: () => i18n.t('Stop Price') }
];
export const getTradeStopPricesDataKeyMetatrader = (): TUITradeConfirmationItemData[] => [
  { key: 'price', label: () => i18n.t('Price') }
];

export const getTradeStopLimitPricesDataKeyHorizon = (): TUITradeConfirmationItemData[] => [
  { key: 'price', label: () => i18n.t('Limit Price') },
  { key: 'priceTrigger', label: () => i18n.t('Stop Price') }
];
export const getTradeStopLimitPricesDataKeyMetatrader = (): TUITradeConfirmationItemData[] => [
  { key: 'price', label: () => i18n.t('Price') },
  { key: 'priceTrigger', label: () => i18n.t('Stop Limit Price') }
];

const getBaseTradeControls = (): TUITradeConfirmationItemData[] => [
  { key: 'stopLoss', label: () => i18n.t('Stop Loss') },
  { key: 'takeProfit', label: () => i18n.t('Take Profit') }
];

export const getTradeControlsDataKeyHorizon = (): TUITradeConfirmationItemData[] => [
  ...getBaseTradeControls(),
  { key: 'fillPolicy', label: () => i18n.t('Time-In-Force') },
  { key: 'expiration', label: () => i18n.t('Expiry Date') }
];
export const getTradeControlsDataKeyMT4 = (): TUITradeConfirmationItemData[] => [
  ...getBaseTradeControls(),
  { key: 'tradeExpiry', label: () => i18n.t('Expiry') },
  { key: 'expiration', label: () => i18n.t('Expiry Date') }
];
export const getTradeControlsDataKeyMT5 = (): TUITradeConfirmationItemData[] => [
  ...getBaseTradeControls(),
  { key: 'tradeExpiry', label: () => i18n.t('Expiry') },
  { key: 'expiration', label: () => i18n.t('Expiry Date') },
  { key: 'fillPolicy', label: () => i18n.t('Fill Policy') }
];
export const getTradeControlsDataKeyCentroid = (): TUITradeConfirmationItemData[] => [
  ...getBaseTradeControls(),
  { key: 'centroidDeviation', label: () => i18n.t('Market Range') },
  { key: 'fillPolicy', label: () => i18n.t('Time-In-Force') }
];

// Backward compatibility exports (non-reactive)
export const TRADE_BASE_PRICES_DATA_KEY_HORIZON = getTradeBasePricesDataKeyHorizon();
export const TRADE_BASE_PRICES_DATA_KEY_MT4 = getTradeBasePricesDataKeyMT4();
export const TRADE_BASE_PRICES_DATA_KEY_MT5 = getTradeBasePricesDataKeyMT5();
export const TRADE_BASE_PRICES_DATA_KEY_CENTROID = getTradeBasePricesDataKeyCentroid();

export const TRADE_LIMIT_PRICES_DATA_KEY_HORIZON = getTradeLimitPricesDataKeyHorizon();
export const TRADE_LIMIT_PRICES_DATA_KEY_METATRADER = getTradeLimitPricesDataKeyMetatrader();

export const TRADE_STOP_PRICES_DATA_KEY_HORIZON = getTradeStopPricesDataKeyHorizon();
export const TRADE_STOP_PRICES_DATA_KEY_METATRADER = getTradeStopPricesDataKeyMetatrader();

export const TRADE_STOP_LIMIT_PRICES_DATA_KEY_HORIZON = getTradeStopLimitPricesDataKeyHorizon();
export const TRADE_STOP_LIMIT_PRICES_DATA_KEY_METATRADER = getTradeStopLimitPricesDataKeyMetatrader();

export const TRADE_CONTROLS_DATA_KEY_HORIZON = getTradeControlsDataKeyHorizon();
export const TRADE_CONTROLS_DATA_KEY_MT4 = getTradeControlsDataKeyMT4();
export const TRADE_CONTROLS_DATA_KEY_MT5 = getTradeControlsDataKeyMT5();
export const TRADE_CONTROLS_DATA_KEY_CENTROID = getTradeControlsDataKeyCentroid();
